<template>
    <div :id="modalId" class="sr-modal-page" :class="[type, loading ? 'loading' : '']">
        <VDialog
            transition="sr-modal-page-transition"
            content-class="sr-modal-page-content-wrapper"
            class="sr-modal-page-dialog"
            :attach="`#${modalId}`"
            :value="showModal"
            :persistent="persistent"
            v-bind="$attrs"
            :retain-focus="false"
        >
            <div v-if="loading" class="sr-modal-page-loading-overlay">
                <VProgressLinear
                    :indeterminate="loadingConfig.indeterminate"
                    :value="loadingConfig.progress * 100"
                    color="blue"
                />
            </div>
            <SrModalPageBasic v-if="type === 'basic'" class="flex-grow-1" @close="close">
                <!-- pass slots to child component -->
                <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
            </SrModalPageBasic>
            <SrModalPageStepper
                v-if="type === 'stepper'"
                v-model="currentStep"
                :modal-id="modalId"
                :can-back="canBack"
                :can-continue="canContinue"
                :tabs="tabs"
                class="flex-grow-1"
                :steps="steps"
                :disable-keyboard-navigation="disableKeyboardNavigation"
                :disable-keyboard-submit="disableKeyboardSubmit"
                @continue-clicked="$emit('continue-clicked')"
                @close="close"
                @submit="submit"
            >
                <!-- pass slots to child component -->
                <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
            </SrModalPageStepper>
            <div class="footer">
                <slot name="footer" />
            </div>
        </VDialog>
    </div>
</template>

<script>
import SrModalPageBasic from './SrModalPageBasic';
import SrModalPageStepper from './SrModalPageStepper';

export const ACTIVE_MODALS_KEY = 'sr-active-modals';

export default {
    name: 'SrModalPage',
    components: { SrModalPageStepper, SrModalPageBasic },
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        /**
         * Returns the currently active tab. Starts at 1
         * @model
         */
        value: {
            type: Number,
            default: 1,
        },
        /**
         * Defines the type of the modal.
         * Available types:
         *  - basic: Provides "header" and "content" slot
         *  - stepper: Provides stepper functionality
         */
        type: {
            type: String,
            default: 'basic',
        },
        /**
         * Controls visibility of modal. Can be used with .sync modifier
         */
        show: {
            type: Boolean,
            required: true,
        },
        /**
         * List of steps.
         * Can be array of strings or array of objects
         * Objects contain the following properties:
         *  - name              string      required
         *  - rules             Array<Fn>   default: []
         *  - completeIcon      string      default: '$complete'
         *  - editIcon          string      default: '$edit'
         *  - errorIcon         string      default: '$error'
         */
        steps: {
            type: Array,
            required: false,
            default: () => [],
        },
        /**
         * Allow user to jump to any step.
         */
        tabs: {
            type: Boolean,
            default: false,
        },
        /**
         * Clicking outside of the element will not deactivate it.
         */
        persistent: {
            type: Boolean,
            default: true,
        },
        /**
         * Shows "X" button in top right corner to close modal.
         */
        closeAble: {
            type: Boolean,
            default: true,
        },
        /**
         * Shows loading animation
         */
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Custom loading configuration for visualizing progress
         */
        loadingConfig: {
            type: Object,
            default: () => ({
                indeterminate: true,
                progress: 0, // 0.0 - 1.0
            }),
        },
        /**
         * Indicates whether the previous step can be navigated to
         */
        canBack: {
            type: Boolean,
            default: true,
        },
        /**
         * Indicates whether the next step can be navigated to
         */
        canContinue: {
            type: Boolean,
            default: true,
        },
        /**
         * Indicates whether the form can be submitted
         */
        canSubmit: {
            type: Boolean,
            default: true,
        },
        /**
         * Disables 'ArrowLeft' and 'ArrowRight' navigation between tabs
         */
        disableKeyboardNavigation: {
            type: Boolean,
            default: false,
        },
        /**
         * Disables 'CTRL + S' for submitting the form
         */
        disableKeyboardSubmit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showModal: this.show,
            currentStep: this.value,
        };
    },
    computed: {
        modalId() {
            return `_${Math.random().toString(36).substr(2, 9)}`;
        },
    },
    watch: {
        value() {
            this.currentStep = this.value;
        },
        currentStep() {
            this.$emit('input', this.currentStep); // update components v-model
        },
        show() {
            this.showModal = this.show;
        },
        showModal(show) {
            this.$emit('update:show', this.showModal); // update "show" property
            this.storeActiveModal(show);
        },
    },
    created() {
        this.storeActiveModal(this.show);
    },
    methods: {
        submit() {
            if (this.canSubmit) {
                this.$emit('submit');
            }
        },
        close() {
            this.$emit('close');
        },
        storeActiveModal(show) {
            if (!window[ACTIVE_MODALS_KEY]) {
                window[ACTIVE_MODALS_KEY] = [];
            }

            if (show) {
                window[ACTIVE_MODALS_KEY].push(this.modalId);
            } else {
                window[ACTIVE_MODALS_KEY].pop();
            }
        },
    },
};
</script>
